import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Breadcrumbs,
  Chip,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import { BoardMark } from './BoardSurfaces';
import { PageBanner } from '../ui/PageBanner';
import { Link as RouterLink } from 'react-router-dom';
import type { BoardCoreFragment } from '../../../generated/graphql';

interface BoardHeaderProps {
  board: BoardCoreFragment;
  currentUserId: string;
  onRename: (newName: string) => void;
  onInvite: () => void;
  onDelete: () => void;
  onReorganizeColumns?: () => void;
}

type MemberSummary = BoardCoreFragment['members'][number];

function memberLabel(m: MemberSummary): string {
  const full = `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim();
  return full || m.email;
}

export function BoardHeader({
  board,
  currentUserId,
  onRename,
  onInvite,
  onDelete,
  onReorganizeColumns,
}: BoardHeaderProps) {
  const isOwner = board.ownerId === currentUserId;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(board.name);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => setDraft(board.name), [board.name]);
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== board.name) onRename(trimmed);
    else setDraft(board.name);
  };

  const closeMenu = () => setMenuAnchor(null);
  const handleInvite = () => {
    closeMenu();
    onInvite();
  };
  const handleDelete = () => {
    closeMenu();
    onDelete();
  };
  const handleReorganize = () => {
    closeMenu();
    onReorganizeColumns?.();
  };

  return (
    <PageBanner data-testid="board-header" variant="hero">
      <Box sx={{ minWidth: 0 }}>
        <Breadcrumbs
          separator="/"
          sx={{
            fontSize: '0.75rem',
            mb: 1,
            color: 'text.secondary',
          }}
        >
          <Box
            component={RouterLink}
            to="/boards"
            sx={{
              color: 'inherit',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Boards
          </Box>
          <Typography variant="caption" color="text.primary">
            {board.name}
          </Typography>
        </Breadcrumbs>
        <Stack
          direction="row"
          alignItems="center"
          useFlexGap
          gap={{ xs: 1, md: 1.5 }}
        >
          <BoardMark
            sx={{
              width: 44,
              height: 44,
              display: { xs: 'none', md: 'inline-flex' },
            }}
          >
            <ViewKanbanOutlinedIcon />
          </BoardMark>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {editing && isOwner ? (
              <TextField
                inputRef={inputRef}
                size="small"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commit();
                  if (e.key === 'Escape') {
                    setEditing(false);
                    setDraft(board.name);
                  }
                }}
              />
            ) : (
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontFamily: 'inherit',
                  fontWeight: 600,
                  fontSize: { xs: 24, md: 28 },
                  lineHeight: 1.25,
                  letterSpacing: '-0.025em',
                  overflowWrap: 'anywhere',
                  cursor: isOwner ? 'pointer' : 'default',
                }}
                onClick={() => isOwner && setEditing(true)}
              >
                {board.name}
              </Typography>
            )}
            <Stack
              direction="row"
              useFlexGap
              flexWrap="wrap"
              gap={1}
              alignItems="center"
              sx={{ mt: 0.5 }}
            >
              <Typography variant="caption" color="text.secondary">
                {board.columns.reduce(
                  (total, column) => total + column.cards.length,
                  0
                )}{' '}
                projects · {board.columns.length} stages
              </Typography>
              {(board.members ?? []).map((m) => (
                <Chip
                  variant="soft"
                  key={m._id}
                  label={memberLabel(m)}
                  size="small"
                  sx={{
                    display: { xs: 'none', md: 'inline-flex' },
                    height: 22,
                    maxWidth: 180,
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </Box>
      <Stack direction="row" alignItems="center" gap={1.5}>
        <Button
          variant="contained"
          startIcon={<PersonAddOutlinedIcon />}
          onClick={onInvite}
          sx={{
            display: { xs: 'none', md: 'inline-flex' },
            flexShrink: 0,
          }}
        >
          Invite member
        </Button>
        <IconButton
          variant="outlined"
          size="small"
          aria-label="board actions"
          sx={{ width: 32, height: 32, flexShrink: 0 }}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={handleInvite}>Invite member</MenuItem>
          {onReorganizeColumns && (
            <MenuItem onClick={handleReorganize}>Edit columns</MenuItem>
          )}
          {isOwner && (
            <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
              Delete board
            </MenuItem>
          )}
        </Menu>
      </Stack>
    </PageBanner>
  );
}
